import React from 'react';
import { Conta, TipoContaId } from '../../types/financeiro';
import { Plus, RefreshCw, AlertCircle } from 'lucide-react';
import { FinanceiroContaRow } from './FinanceiroContaRow';
import { FinanceiroContaCard } from './FinanceiroContaCard';
import { Pagination } from '../common/Pagination';

interface FinanceiroSaidasSectionProps {
  contas: Conta[];
  paginatedContas: Conta[];
  pessoasMap: Map<number, string>;
  loading: boolean;
  currentPage: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (items: number) => void;
  onCreate: () => void;
  onView: (conta: Conta) => void;
  onEdit: (conta: Conta) => void;
  onDelete: (conta: Conta) => void;
  onBaixa: (conta: Conta) => void;
  onViewComprovante: (conta: Conta) => void;
}

export function FinanceiroSaidasSection({
  contas,
  paginatedContas,
  pessoasMap,
  loading,
  currentPage,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  onCreate,
  onView,
  onEdit,
  onDelete,
  onBaixa,
  onViewComprovante,
}: FinanceiroSaidasSectionProps) {
  return (
    <div className="bg-white shadow-xs rounded-2xl overflow-hidden border border-slate-200">
      {/* Visão Mobile (Cards) */}
      <div className="block md:hidden p-3 space-y-3">
        {loading ? (
          <div className="py-12 text-center text-sm text-slate-500">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-slate-400" />
            Carregando contas a pagar...
          </div>
        ) : contas.length === 0 ? (
          <div className="py-8 text-center">
            <AlertCircle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm font-bold text-slate-800">Nenhuma conta a pagar encontrada</p>
            <button
              onClick={onCreate}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 text-white rounded-xl text-xs font-bold"
            >
              <Plus className="w-4 h-4" /> Nova Conta a Pagar
            </button>
          </div>
        ) : (
          paginatedContas.map((c) => (
            <FinanceiroContaCard
              key={`card-pagar-${c.id}`}
              conta={c}
              pessoaNome={pessoasMap.get(c.pessoa_id) || `Fornecedor #${c.pessoa_id}`}
              isReceita={false}
              onView={onView}
              onEdit={onEdit}
              onDelete={onDelete}
              onBaixa={onBaixa}
              onViewComprovante={onViewComprovante}
            />
          ))
        )}
      </div>

      {/* Visão Desktop / Tablet (Tabela Completa) */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">ID</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Descrição & NF</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Favorecido / Fornecedor</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Vencimento</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Pagamento</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Valor R$</th>
              <th className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Anexo</th>
              <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-sm text-slate-500">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-slate-400" />
                  Carregando contas a pagar...
                </td>
              </tr>
            ) : contas.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center">
                  <div className="max-w-md mx-auto flex flex-col items-center justify-center">
                    <AlertCircle className="w-10 h-10 text-slate-400 mb-2" />
                    <p className="text-base font-bold text-slate-800">Nenhuma conta a pagar encontrada</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Nenhum lançamento de despesa corresponde aos filtros ativos.
                    </p>
                    <button
                      onClick={onCreate}
                      className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold shadow-2xs transition-colors cursor-pointer"
                    >
                      <Plus className="w-4 h-4" /> Cadastrar Conta a Pagar
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedContas.map((c) => (
                <FinanceiroContaRow
                  key={`row-pagar-${c.id}`}
                  conta={c}
                  pessoaNome={pessoasMap.get(c.pessoa_id) || `Fornecedor #${c.pessoa_id}`}
                  isReceita={false}
                  onView={onView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onBaixa={onBaixa}
                  onViewComprovante={onViewComprovante}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {contas.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={contas.length}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      )}
    </div>
  );
}
